// src/ContractsDisplay.js
import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Table,
  Input,
  Spin,
  Alert,
  Card,
  Row,
  Col,
  Typography,
  Statistic,
  Tag,
  Tooltip as AntTooltip,
  Layout,
} from "antd"; // Added Layout
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { format, parseISO, isValid } from "date-fns"; // Added isValid

const { Search } = Input;
const { Title, Text } = Typography;

// Function to safely format currency
const formatCurrency = (value) => {
  // Handle potential null or non-string values before parsing
  const stringValue =
    value === null || typeof value === "undefined" ? "" : String(value);
  const number = parseFloat(stringValue.replace(/,/g, "")); // Remove existing commas if any
  if (isNaN(number)) {
    return "N/A";
  }
  return number.toLocaleString("en-CA", { style: "currency", currency: "CAD" });
};

// Function to safely format dates (more robust)
const formatDate = (dateString) => {
  if (!dateString || typeof dateString !== "string") return "N/A";
  try {
    // Attempt parsing common formats, ISO is preferred
    const date = parseISO(dateString); // Handles YYYY-MM-DD
    if (isValid(date)) {
      return format(date, "MMM dd, yyyy");
    }
    // Add other potential parse attempts if needed, e.g., different formats
    // console.warn("Could not parse date string:", dateString);
    return dateString; // Return original if parsing fails or format unknown
  } catch (error) {
    console.warn("Error parsing date:", dateString, error);
    return dateString; // Return original on error
  }
};

function ContractsDisplay() {
  const [allData, setAllData] = useState([]); // Holds the entire dataset as objects
  const [filteredData, setFilteredData] = useState([]); // Data after filtering
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 15,
    total: 0,
    showSizeChanger: true,
    pageSizeOptions: ["15", "30", "50", "100", "200"], // Added 200
  });

  // --- Data Fetching and Processing ---
  useEffect(() => {
    console.log("Fetching data...");
    setLoading(true);
    fetch("/data.json") // Fetching from the public folder
      .then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        console.log("Data received, parsing JSON...");
        return response.json();
      })
      .then((data) => {
        console.log(`Data parsed successfully.`);

        // --- DATA TRANSFORMATION LOGIC ---
        if (
          !data ||
          !Array.isArray(data.fields) ||
          !Array.isArray(data.records)
        ) {
          throw new Error(
            "Invalid data structure: 'fields' or 'records' array not found or not an array."
          );
        }

        const fields = data.fields;
        const records = data.records;
        console.log(
          `Fields found: ${fields.length}, Records found: ${records.length}`
        );

        if (fields.length === 0) {
          // No need to check records.length here, map handles empty array
          console.warn("Fields array is empty. Cannot process records.");
          setAllData([]);
          setFilteredData([]);
          setPagination((prev) => ({ ...prev, total: 0 }));
          setError(null);
          setLoading(false);
          return;
        }

        // Create a map of field IDs to their index for easier lookup (optional but can be clearer)
        // const fieldIndexMap = fields.reduce((map, field, index) => {
        //     map[field.id] = index;
        //     return map;
        // }, {});

        // Map array records to objects
        const processedData = records.map((record, index) => {
          const recordObject = {};
          fields.forEach((field, fieldIndex) => {
            // Get the value from the record array using the field's index
            // Handle cases where record might be shorter than fields array (robustness)
            const value =
              record.length > fieldIndex ? record[fieldIndex] : null; // Use null for missing values
            recordObject[field.id] = value; // Assign value to the object using field id as key
          });

          // Add unique key (use reference_number if available, fallback to index)
          // Check if the reference_number field exists and has a value
          const refNumIndex = fields.findIndex(
            (f) => f.id === "reference_number"
          );
          const refNum =
            refNumIndex !== -1 && record.length > refNumIndex
              ? record[refNumIndex]
              : null;
          recordObject.key = refNum || `record-${index}`;

          // Add pre-parsed numeric values for sorting/calculations
          recordObject.contract_value_num =
            parseFloat(recordObject.contract_value) || 0;
          recordObject.original_value_num =
            parseFloat(recordObject.original_value) || 0;
          recordObject.amendment_value_num =
            parseFloat(recordObject.amendment_value) || 0;

          return recordObject; // Return the created object
        });
        // --- END DATA TRANSFORMATION ---

        console.log(`Processed ${processedData.length} records into objects.`);
        setAllData(processedData);
        setFilteredData(processedData);
        setPagination((prev) => ({ ...prev, total: processedData.length }));
        setError(null);
      })
      .catch((err) => {
        console.error("Failed to fetch or process data:", err);
        setError(
          `Failed to load contract data: ${err.message}. Check console for details.`
        );
        setAllData([]);
        setFilteredData([]);
      })
      .finally(() => {
        console.log("Fetching/Processing finished.");
        setLoading(false);
      });
  }, []); // Empty dependency array ensures this runs only once on mount

  // --- Filtering Logic (Debounced) ---
  const debounce = (func, delay) => {
    let timeoutId;
    return function (...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  };

  const applyFilter = useCallback(
    (currentSearchTerm) => {
      if (!allData || allData.length === 0) return; // Don't filter if no data

      const lowerSearchTerm = (currentSearchTerm || "").toLowerCase();

      if (!lowerSearchTerm) {
        // Reset if search term is empty
        setFilteredData(allData);
        setPagination((prev) => ({
          ...prev,
          total: allData.length,
          current: 1,
        }));
        return;
      }

      console.log("Applying filter:", lowerSearchTerm);
      setLoading(true); // Indicate filtering activity

      // Use setTimeout to allow UI update before potentially long filter
      setTimeout(() => {
        const results = allData.filter((item) => {
          // Search across relevant TEXT fields (adjust as needed)
          return (
            (item.vendor_name &&
              String(item.vendor_name)
                .toLowerCase()
                .includes(lowerSearchTerm)) ||
            (item.description_en &&
              String(item.description_en)
                .toLowerCase()
                .includes(lowerSearchTerm)) ||
            (item.reference_number &&
              String(item.reference_number)
                .toLowerCase()
                .includes(lowerSearchTerm)) ||
            (item.procurement_id &&
              String(item.procurement_id)
                .toLowerCase()
                .includes(lowerSearchTerm)) ||
            (item.economic_object_code &&
              String(item.economic_object_code).includes(lowerSearchTerm)) || // Might not need lowerCase
            (item.comments_en &&
              String(item.comments_en)
                .toLowerCase()
                .includes(lowerSearchTerm)) ||
            (item.owner_org_title &&
              String(item.owner_org_title)
                .toLowerCase()
                .includes(lowerSearchTerm)) // Added org title search
          );
        });
        setFilteredData(results);
        setPagination((prev) => ({
          ...prev,
          total: results.length,
          current: 1,
        })); // Reset page to 1 on new search
        setLoading(false);
        console.log("Filter applied, results:", results.length);
      }, 50); // Small delay
    },
    [allData]
  ); // Dependency: allData

  const debouncedFilter = useMemo(
    () => debounce(applyFilter, 500),
    [applyFilter]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    debouncedFilter(value);
  };

  const handleSearchEnter = (value) => {
    setSearchTerm(value);
    // Cancel any pending debounced search and run immediately
    clearTimeout(debouncedFilter); // Assuming debounce returns an object with cancel or similar, if not, this line might not work perfectly depending on debounce implementation. Standard setTimeout approach doesn't need this cancel.
    applyFilter(value);
  };

  // --- Table Columns Definition ---
  // (Column definitions remain largely the same as they refer to the object keys
  // created during the data transformation)
  const columns = useMemo(
    () => [
      {
        title: "Ref / Proc. ID",
        dataIndex: "reference_number",
        key: "ref",
        width: 150,
        sorter: (a, b) =>
          String(a.reference_number || "").localeCompare(
            String(b.reference_number || "")
          ),
        render: (text, record) => (
          <AntTooltip
            title={`Procurement ID: ${record.procurement_id || "N/A"}`}
          >
            <span>{text || "N/A"}</span>
          </AntTooltip>
        ),
        ellipsis: true,
      },
      {
        title: "Vendor",
        dataIndex: "vendor_name",
        key: "vendor",
        width: 250,
        sorter: (a, b) =>
          String(a.vendor_name || "").localeCompare(
            String(b.vendor_name || "")
          ),
        ellipsis: true,
      },
      {
        title: "Org", // Added Owning Org
        dataIndex: "owner_org_title",
        key: "owner_org_title",
        width: 200,
        sorter: (a, b) =>
          String(a.owner_org_title || "").localeCompare(
            String(b.owner_org_title || "")
          ),
        ellipsis: true,
      },
      {
        title: "Contract Date",
        dataIndex: "contract_date",
        key: "contract_date",
        width: 120,
        render: formatDate,
        // Sorter needs to handle potentially invalid dates robustly
        sorter: (a, b) => {
          const dateA = a.contract_date ? parseISO(a.contract_date) : null;
          const dateB = b.contract_date ? parseISO(b.contract_date) : null;
          if (isValid(dateA) && isValid(dateB)) return dateA - dateB;
          if (isValid(dateA)) return -1; // Valid dates first
          if (isValid(dateB)) return 1;
          return String(a.contract_date || "").localeCompare(
            String(b.contract_date || "")
          ); // Fallback string compare
        },
      },
      {
        title: "Description",
        dataIndex: "description_en",
        key: "description",
        ellipsis: true,
        sorter: (a, b) =>
          String(a.description_en || "").localeCompare(
            String(b.description_en || "")
          ),
        // render: (text) => <AntTooltip title={text}><span>{text || 'N/A'}</span></AntTooltip> // Optional tooltip
      },
      {
        title: "Type",
        dataIndex: "instrument_type",
        key: "instrument_type",
        width: 100,
        filters: [
          { text: "Contract", value: "C" },
          { text: "Amendment", value: "A" },
          { text: "SOSA", value: "SOSA" },
        ],
        onFilter: (value, record) => record.instrument_type === value,
        render: (type) => {
          let color = "default";
          let text = type || "N/A";
          if (type === "A") {
            color = "orange";
            text = "Amendment";
          } else if (type === "C") {
            color = "green";
            text = "Contract";
          } else if (type === "SOSA") {
            color = "purple";
            text = "SOSA";
          }
          return <Tag color={color}>{text}</Tag>;
        },
      },
      {
        title: "Contract Value",
        dataIndex: "contract_value", // Display original text value
        key: "value",
        width: 150,
        align: "right",
        render: formatCurrency, // Format the text value for display
        sorter: (a, b) => a.contract_value_num - b.contract_value_num, // Sort by pre-calculated number
      },
      {
        title: "Original Value",
        dataIndex: "original_value",
        key: "original_value",
        width: 150,
        align: "right",
        render: formatCurrency,
        sorter: (a, b) => a.original_value_num - b.original_value_num,
      },
      {
        title: "Amendment Value",
        dataIndex: "amendment_value",
        key: "amendment_value",
        width: 150,
        align: "right",
        render: formatCurrency,
        sorter: (a, b) => a.amendment_value_num - b.amendment_value_num,
      },
      // Add other columns as needed, e.g., 'comments_en' with ellipsis: true
    ],
    []
  );

  // --- Handle Table Changes ---
  const handleTableChange = (newPagination, filters, sorter) => {
    console.log("Table change:", newPagination, filters, sorter);
    // For client-side, antd handles sorting/filtering based on column definitions.
    // We only need to update pagination state.
    setPagination(newPagination);
  };

  // --- Chart Data Preparation ---
  const chartData = useMemo(() => {
    // Aggregate based on filtered data. Still limit for performance demo.
    // Consider aggregating `allData` in a worker if needed for full overview.
    const dataToAggregate = filteredData.slice(0, 2000); // Increase sample size slightly

    if (!dataToAggregate || dataToAggregate.length === 0) return [];

    // Group by Owning Org Title for example
    const aggregated = dataToAggregate.reduce((acc, item) => {
      const org = item.owner_org_title || "Unknown Org";
      if (!acc[org]) {
        acc[org] = { org: org, totalValue: 0, count: 0 };
      }
      // Use the pre-calculated numeric value
      acc[org].totalValue += item.contract_value_num;
      acc[org].count += 1;
      return acc;
    }, {});

    // Get top N orgs by value for the chart to keep it readable
    const topOrgs = Object.values(aggregated)
      .sort((a, b) => b.totalValue - a.totalValue)
      .slice(0, 15); // Show top 15

    return topOrgs;
  }, [filteredData]); // Recalculate chart data when filteredData changes

  // --- Summary Statistics ---
  const summaryStats = useMemo(() => {
    const count = filteredData.length;
    // Use pre-calculated numbers for sum
    const totalValue = filteredData.reduce(
      (sum, item) => sum + item.contract_value_num,
      0
    );
    return {
      count,
      totalValue,
    };
  }, [filteredData]);

  return (
    // Using Layout.Content provided by App.js structure
    <>
      <Title level={2} style={{ marginBottom: "20px" }}>
        Government Contract Data
      </Title>

      <Alert
        message="Performance Note"
        description="Loading and filtering the full dataset may be slow on some devices due to the large number of records (1.1M+). Results are paginated for display."
        type="info" // Changed from warning to info
        showIcon
        closable
        style={{ marginBottom: 16 }}
      />

      {error && (
        <Alert
          message="Error Loading Data"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card bordered={false}>
            <Statistic
              title="Filtered Records"
              value={loading ? "-" : summaryStats.count.toLocaleString()}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={10}>
          <Card bordered={false}>
            <Statistic
              title="Total Value (Filtered)"
              value={loading ? "-" : summaryStats.totalValue}
              precision={2}
              formatter={(val) =>
                `$${Number(val).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`
              } // Ensure localeString formatting
            />
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Search
            placeholder="Search Vendor, Org, Desc, ID..."
            allowClear
            enterButton
            size="large"
            onSearch={handleSearchEnter} // Use immediate search on Enter
            onChange={handleSearchChange} // Debounced search on typing
            loading={loading && searchTerm !== ""}
            value={searchTerm}
            style={{ width: "100%" }} // Ensure search takes full column width
          />
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24}>
          <Card title="Top 15 Orgs by Total Contract Value (Sample from Filtered Data)">
            <Spin
              spinning={loading && chartData.length === 0}
              tip="Loading Chart..."
            >
              {!loading && chartData.length === 0 ? (
                <Text>
                  No data available for chart based on current filter or sample.
                </Text>
              ) : (
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={chartData}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 150, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      tickFormatter={(value) =>
                        `$${(value / 1000000).toFixed(1)}M`
                      }
                    />
                    <YAxis
                      dataKey="org"
                      type="category"
                      width={150}
                      interval={0}
                    />
                    <Tooltip formatter={(value) => formatCurrency(value)} />
                    <Legend />
                    <Bar
                      dataKey="totalValue"
                      fill="#8884d8"
                      name="Total Contract Value"
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Spin>
          </Card>
        </Col>
      </Row>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1600 /*, y: 600 */ }} // Adjusted horizontal scroll width
          size="small"
          rowKey="key"
          bordered
        />
      </Card>
    </> // Closing fragment
  );
}

export default ContractsDisplay;
